/** @format */

import React, { useState, useEffect, useMemo } from "react";
import LabelInput from "../../../shared/components/labelInput";
import DriverNameFields from "../../../shared/components/driverNameFields";
import { useFormContext } from "react-hook-form";
import {
  fetchEmp,
  fetchEmpBusiness,
  fetchLicenceAuth,
  fetchMartialStatus,
  fetchOccupation,
  fetchRating,
  fetchRelation,
  fetchTitlesService,
  fetchUberBranch,
  fetchVehicleUse,
} from "../../../shared/services/simpleListItemsService";
import LicenseModal from "../../../shared/components/licenseModal";
import TqAddDriverMapper from "../mappers/tqAddDriverMapper";
import TqAddDriverClaim from "./tqAddDriverClaim";
import NewDatePicker from "../../../shared/components/newDatePicker";
import { generateDLN } from "../../../shared/utils/generateDLN";
import MonthYearField from "../../../shared/components/monthYearField";
import RadioGroup from "../../../shared/components/RadioGroup";
import ModalWrapper from "../../../shared/components/ModalWrapper";

const TqAddDriverModal = ({
  onDriverSaved,
  isModalOpen,
  setIsModalOpen,
  localClaimFields,
  setLocalClaimFields,
}) => {
  const {
    register,
    formState: { errors },
    watch,
    setValue,
    control,
    handleSubmit,
    trigger,
    reset,
  } = useFormContext();

  const [titles, setTitles] = useState([]);
  const [occupation, setOccupation] = useState([]);
  const [martial, setMartial] = useState([]);
  const [uberBranch, setUberBranch] = useState([]);
  const [licenceAuth, setLicenceAuth] = useState([]);
  const [employeeStatus, setEmployeeStatus] = useState([]);
  const [relation, setRelation] = useState([]);
  const [vehUse, setVehUse] = useState([]);
  const [employeeBusiness, setEmployeeBusiness] = useState([]);
  const [rating, setRating] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [IsEditLicense, setIsEditLicense] = useState(false);

  const dob = watch("addDob_Date");
  const day = dob ? String(new Date(dob).getDate()).padStart(2, "0") : null;
  const month = dob
    ? String(new Date(dob).getMonth() + 1).padStart(2, "0")
    : null;
  const year = dob ? String(new Date(dob).getFullYear()) : null;
  const lastName = watch("addDriver_lastName");
  const gender = watch("addDriverTitle.gender");

  const prioritizedOccupations = ["Taxi Driver", "Taxi Operating", "Chauffeur"];

  const prioritizeOccupations = (array) => {
    const priorityItems = [];
    const otherItems = [];

    array.forEach((item) => {
      if (prioritizedOccupations.includes(item.listItemDescription)) {
        priorityItems.push(item);
      } else {
        otherItems.push(item);
      }
    });

    priorityItems.sort((a, b) => {
      return (
        prioritizedOccupations.indexOf(a.listItemDescription) -
        prioritizedOccupations.indexOf(b.listItemDescription)
      );
    });

    return [...priorityItems, ...otherItems];
  };

  const convertArrayFormat2 = (array) => {
    if (!Array.isArray(array)) {
      return [];
    }

    const prioritizedArray = prioritizeOccupations(array);

    return prioritizedArray.map((item) => ({
      value: item.listItemId,
      label: item.listItemDescription,
    }));
  };

  const convertArrayFormat = (array) => {
    return array.map((item) => ({
      value: item.listItemId,
      label: item.listItemDescription,
      gender: item.listItemExtraInfoValue === "M" ? "Male" : "Female",
    }));
  };

  const sortTitles = (titles) => {
    return titles.sort((a, b) => {
      const aDescription = a.listItemDescription;
      const bDescription = b.listItemDescription;

      if (
        (aDescription === "Mr" || aDescription === "Mrs") &&
        !(bDescription === "Mr" || bDescription === "Mrs")
      ) {
        return -1;
      } else if (
        !(aDescription === "Mr" || aDescription === "Mrs") &&
        (bDescription === "Mr" || bDescription === "Mrs")
      ) {
        return 1;
      } else if (
        aDescription.startsWith("M") &&
        !bDescription.startsWith("M")
      ) {
        return -1;
      } else if (
        !aDescription.startsWith("M") &&
        bDescription.startsWith("M")
      ) {
        return 1;
      }
      return 0;
    });
  };

  useEffect(() => {
    if (dob && lastName && gender) {
      const newDLN = generateDLN(lastName, dob, gender);
      setValue("addlicense", newDLN);
    }
  }, [dob, lastName, gender, setValue]);

  // Fetch data effects
  useEffect(() => {
    const fetchAndSetTitles = async () => {
      try {
        setIsLoading(true);
        const titles = await fetchTitlesService();
        const sortedTitles = sortTitles(titles);
        setTitles(sortedTitles);
      } catch (error) {
        console.error("Error updating titles:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAndSetTitles();
  }, []);

  useEffect(() => {
    fetchOccupation().then((occupations) => setOccupation(occupations));
    fetchUberBranch().then((uber) => setUberBranch(uber));
    fetchMartialStatus().then((statuses) => setMartial(statuses));
    fetchLicenceAuth().then((statuses) => setLicenceAuth(statuses));
    fetchEmp().then((statuses) => setEmployeeStatus(statuses));
    fetchEmpBusiness().then((statuses) => setEmployeeBusiness(statuses));
    fetchRelation().then((statuses) => setRelation(statuses));
    fetchVehicleUse().then((statuses) => setVehUse(statuses));
    fetchRating().then((statuses) => setRating(statuses));
  }, []);

  useEffect(() => {
    if (watch("occupation")?.value === "D34") {
      setValue("addDriverBusinessOccupation", { value: "703", label: "" });
    }
  }, [watch("occupation")]);

  const handleClaimsUpdate = (updatedClaims) => {
    setLocalClaimFields(updatedClaims);
  };

  const onSubmit = handleSubmit((data) => {
    const driverData = {
      addDriverTitle: data.addDriverTitle,
      firstName: data.addDriver_firstName,
      lastName: data.addDriver_lastName,
      addDob_Date: data.addDob_Date,
      addlicense: data.addlicense,
      addlicenseUser: data.addlicenseUser,
      rating: data.rating,
      occupation: data.occupation,
      addDriverempStatus: data.addDriverempStatus,
      addDriverBusinessOccupation: data.addDriverBusinessOccupation,
      addDriverpartTimeOcc: data.addDriverpartTimeOcc,
      addDriverpartTimeBusinessOcc: data.addDriverpartTimeBusinessOcc,
      addDriverPartTimeEmpStatus: data.addDriverPartTimeEmpStatus,
      parttime: data.parttime,
      residecy: data.residecy,
      residencyYears: data.residencyYears,
      martialStatus: data.martialStatus,
      AddDriverbatch_Month: data.AddDriverbatch_Month,
      AddDriverbatch_Year: data.AddDriverbatch_Year,
      relation: data.relation,
      addvehicleUse: data.addvehicleUse,
      index: watch("driverIndex"),
      additionalDriverClaims: localClaimFields,
    };

    TqAddDriverMapper.set(driverData);
    onDriverSaved();
    setIsModalOpen(false);
    reset();
  });

  return (
    <>
      {isModalOpen && (
        <form onSubmit={onSubmit}>
          <ModalWrapper
            heading={
              watch("driverIndex") !== null
                ? "Edit Driver"
                : "Add Additional Driver"
            }
            buttonText="Save Details"
            onClose={() => setIsModalOpen(false)}
            buttonType="submit"
          >
            <div>
              <p className="licPara margin-bottom-20">
                You can only add drivers who hold a valid Taxi badge.
              </p>
              <LabelInput
                id="addDriverTitle"
                label="Title"
                isLoading={isLoading}
                type="select"
                placeholder="e.g Mrs"
                options={convertArrayFormat(titles)}
                register={register("addDriverTitle", {
                  required: "The field is Required",
                })}
                errors={errors.addDriverTitle}
                watch={watch("addDriverTitle")}
                control={control}
                trigger={trigger}
              />
              <DriverNameFields
                {...{
                  register,
                  errors,
                  watch,
                  trigger,
                  prefix: "addDriver_",
                }}
              />
              <NewDatePicker
                name="addDob_Date"
                label="Date of birth"
                errors={errors.addDob_Date}
                setValue={setValue}
                control={control}
                rules={{
                  required: "Date is required",
                }}
                watch={watch}
                trigger={trigger}
                maxDate={new Date()}
              />
              <label className="labelName">Driving licence number</label>
              <p className="licPara">
                We require your Driving Licence Number ("DLN") to provide this
                to the DVLA to confirm your licence status, entitlement and
                restriction information and endorsement/conviction data.
              </p>
              <div className="twoDivs">
                <LabelInput
                  id="addlicense"
                  type="text"
                  placeholder="xxxxxxxxxxx"
                  disabled={true}
                  maxLength={11}
                  register={register("addlicense", {
                    required: "The field is Required",
                    pattern: {
                      value: /^[a-zA-Z0-9]{11}$/,
                      message: "Invalid format (xxxxxxxxxxx)",
                    },
                  })}
                  errors={errors.addlicense}
                  trigger={trigger}
                  watch={watch("addlicense")}
                  classNamelic="licFieldAuto"
                  classLickField="lickFieldClass"
                />
                <LabelInput
                  id="addlicenseUser"
                  type="text"
                  placeholder="xxxxx"
                  maxLength={5}
                  register={register("addlicenseUser", {
                    required: "The field is Required",
                    pattern: {
                      value: /^[a-zA-Z0-9]{5}$/,
                      message: "Invalid format (xxxxx)",
                    },
                  })}
                  errors={errors.addlicenseUser}
                  trigger={trigger}
                  watch={watch("addlicenseUser")}
                  classNamelic="licFieldAuto"
                  classLickField="lickFieldUserClass"
                />

                <button
                  onClick={() => setIsOpen(true)}
                  className="FindButton FindButton2"
                  type="button"
                >
                  Need help?
                </button>
              </div>

              <LicenseModal
                openModal={() => setIsOpen(true)}
                isOpen={isOpen}
                closeModal={() => setIsOpen(false)}
              />

              <LabelInput
                id="rating"
                label="Uber Pro rating"
                type="select"
                placeholder="Select rating"
                control={control}
                options={convertArrayFormat(rating)}
                register={register("rating", {
                  required: "The field is Required",
                })}
                errors={errors.rating}
                watch={watch("rating")}
                trigger={trigger}
              />

              <LabelInput
                id="relation"
                label="What is your relationship to the proposer?"
                type="select"
                placeholder="Select relationship"
                control={control}
                options={convertArrayFormat(relation)}
                register={register("relation", {
                  required: "The field is Required",
                })}
                errors={errors.relation}
                watch={watch("relation")}
                trigger={trigger}
              />

              <RadioGroup
                name="addvehicleUse"
                mainLabel="In addition to working Private Hire for Uber, does the additional driver work Public Hire or as a Hackney Carriage?"
                register={register}
                errors={errors.addvehicleUse}
                yesValue={17}
                noValue={15}
              />

              <LabelInput
                id="occupation"
                label="Occupation"
                type="select"
                options={convertArrayFormat2(occupation)}
                control={control}
                placeholder="Select occupation"
                register={register("occupation", { required: true })}
                errors={errors.occupation}
                watch={watch("occupation")}
                trigger={trigger}
                isSearchable={true}
              />
              <LabelInput
                id="addDriverBusinessOccupation"
                label="Business occupation"
                type="select"
                options={convertArrayFormat(employeeBusiness)}
                control={control}
                placeholder="Select business occupation"
                register={register("addDriverBusinessOccupation", {
                  required: true,
                })}
                errors={errors.addDriverBusinessOccupation}
                watch={watch("addDriverBusinessOccupation")}
                trigger={trigger}
                isSearchable={true}
              />

              <LabelInput
                id="addDriverempStatus"
                label="Employment status"
                type="select"
                options={convertArrayFormat(employeeStatus)}
                control={control}
                placeholder="Select status"
                register={register("addDriverempStatus", {
                  required: true,
                })}
                errors={errors.addDriverempStatus}
                watch={watch("addDriverempStatus")}
                trigger={trigger}
              />

              <RadioGroup
                name="parttime"
                mainLabel="Do you have part-time occupation?"
                register={register}
                errors={errors.parttime}
              />

              {watch("parttime") === "Yes" && (
                <>
                  <LabelInput
                    id="addDriverpartTimeOcc"
                    label="Part time occupation"
                    type="select"
                    options={convertArrayFormat2(occupation)}
                    control={control}
                    placeholder="Select part time occupation"
                    register={register("addDriverpartTimeOcc", {
                      required: true,
                    })}
                    errors={errors.addDriverpartTimeOcc}
                    watch={watch("addDriverpartTimeOcc")}
                    trigger={trigger}
                    isSearchable={true}
                  />

                  <LabelInput
                    id="addDriverpartTimeBusinessOcc"
                    label="Part time business type"
                    type="select"
                    options={convertArrayFormat(employeeBusiness)}
                    control={control}
                    placeholder="Select part time business type"
                    register={register("addDriverpartTimeBusinessOcc", {
                      required: true,
                    })}
                    errors={errors.addDriverpartTimeBusinessOcc}
                    watch={watch("addDriverpartTimeBusinessOcc")}
                    trigger={trigger}
                    isSearchable={true}
                  />
                  <LabelInput
                    id="addDriverPartTimeEmpStatus"
                    label="Part time employee status "
                    type="select"
                    options={convertArrayFormat(employeeStatus)}
                    control={control}
                    placeholder="Select part time employee status"
                    register={register("addDriverPartTimeEmpStatus", {
                      required: true,
                    })}
                    errors={errors.addDriverPartTimeEmpStatus}
                    watch={watch("addDriverPartTimeEmpStatus")}
                    trigger={trigger}
                  />
                </>
              )}

              <RadioGroup
                name="residecy"
                mainLabel="Have You been resident in the UK since birth?"
                register={register}
                errors={errors.residecy}
              />

              {watch("residecy") === "No" && (
                <LabelInput
                  id="residencyYears"
                  label="How many years have you been resident in the UK?"
                  type="number"
                  placeholder="Enter years"
                  pattern="[0-9]*"
                  register={register("residencyYears", {
                    required: "This field is required",
                    pattern: {
                      value: /^\d{1,2}$/,
                      message: "The maximum number of digits is 2",
                    },
                  })}
                  errors={errors.residencyYears}
                  watch={watch("residencyYears")}
                  trigger={trigger}
                />
              )}

              <LabelInput
                id="martialStatus"
                label="Marital status"
                type="select"
                options={convertArrayFormat(martial)}
                control={control}
                placeholder="Select martial status"
                register={register("martialStatus", {
                  required: true,
                })}
                errors={errors.martialStatus}
                watch={watch("martialStatus")}
                trigger={trigger}
              />
              <label className="labelName">
                When did you obtain your Private Hire License or Taxi Badge?
              </label>
              <MonthYearField
                control={control}
                monthID="AddDriverbatch_Month"
                yearID="AddDriverbatch_Year"
                errors={
                  errors.AddDriverbatch_Month || errors.AddDriverbatch_Year
                }
                trigger={trigger}
                watch={watch}
                register={register}
                setValue={setValue}
              />
              {/* <NewDatePicker
              name="AddDriverbatch_Date"
              label="When did you obtain your taxi badge?"
              errors={errors}
              setValue={setValue}
              control={control}
              rules={{
                required: "Date is required",
              }}
              watch={watch}
              maxDate={new Date()}
            /> */}
              <TqAddDriverClaim
                onClaimsUpdate={handleClaimsUpdate}
                localClaimFields={localClaimFields}
                setLocalClaimFields={setLocalClaimFields}
              />
            </div>
          </ModalWrapper>
        </form>
      )}
    </>
  );
};

export default TqAddDriverModal;
